<template>

  <v-dialog
      scrollable
      persistent
      :fullscreen="fullscreen"
      max-width="580"
      class="dialog-reschedule-order-button"
      v-model="isDialogOpen">

    <!-- (activator button) -->
    <template v-slot:activator="{ on, attrs }">
      <span v-bind="attrs" v-on="on" v-if="buttonStyle === 'text'">
        {{ buttonText }}
      </span>
      <v-btn
          v-else-if="buttonStyle === 'menu-item'"
          text
          :width="buttonWidth"
          class="d-flex align-center justify-start"
          color="grey lighten-2"
          v-bind="attrs"
          v-on="on">
        <span>{{ buttonText }}</span>
      </v-btn>
      <!-- Default style -->
      <v-btn
          v-else
          large
          outlined
          :width="buttonWidth"
          color="red darken-4"
          v-bind="attrs"
          v-on="on">
        <v-icon style="font-size:22px;" left>mdi-repeat-variant</v-icon>
        <span>{{ buttonText }}</span>
      </v-btn>
    </template>

    <!-- (dialog content) -->
    <v-card max-width="580px" class="py-0">
      <!-- Title -->
      <v-card-title class="text-h5 py-4">
        <!-- <v-icon style="font-size:23px" color="amber darken-2" class="pr-4">mdi-calendar-refresh</v-icon> -->
        <span class="mr-3">Reschedule Order:</span><span class="order-number">{{ order.order_number }}</span>
      </v-card-title>

      <!-- orange accent-3 -->
      <v-card-text class="px-6 dialog-text">
        <!-- From / To -->
        <div class="to-from-text mt-3">
          {{ shipper.name }} <v-icon color="grey darken-2">mdi-arrow-right-thin</v-icon> {{ receiver.name }}
        </div>
        <div>
          <!-- Reschedule info -->
          <div class="info-text-main mt-8 d-flex align-center">
            <v-icon color="orange accent-3" class="pr-2">mdi-information-outline</v-icon>
            <span>Upon rescheduling an order:</span>
          </div>
          <div class="info-text-sub mt-5 pr-4">
            <ul>
              <li style="color:#ff9002">
                <span style="color:#bcbbbb">Order will be removed from the existing route and the route will be re-calculated.</span>
              </li>
            </ul>
          </div>
          <div class="info-text-sub mt-4 pr-4">
            <ul>
              <li style="color:#ff9002">
                <span style="color:#bcbbbb">Order will become "ready to schedule" to be handled in a future route.</span>
              </li>
            </ul>
          </div>

          <!------------------------------------------------------------------>
          <!-- Client Coords & Route Change Info (for in-transit scenarios) -->
          <!------------------------------------------------------------------>
          <template v-if="requiresRouteChange">
            <div class="mt-6 px-2 py-3 client-coords-container">
              <client-geo-location
                  ref="client-geo-location"
                  :mainMessage="routeChangeMessage"
                  :enableTrueReRoute="enableTrueReRoute"
                  @canceled="cancel" />
            </div>
            <template v-if="!enableTrueReRoute">
              <!-- Route change details -->
              <div class="mt-8">Describe the change to your route:</div>
              <div style="max-width:640px;" class="mt-6">
                <v-textarea
                    rows="1"
                    outlined
                    required
                    auto-grow
                    :rules="$root.rules.fieldRequired"
                    hide-details="auto"
                    v-model.trim="notesRouteChange" />
              </div>
            </template>
          </template>

          <!-- Reason for reschedule -->
          <div class="mt-8">Reason for reschedule:</div>
          <div style="max-width:640px;" class="mt-6">
            <v-textarea
                rows="1"
                outlined
                auto-grow
                hide-details="auto"
                v-model.trim="notesReason" />
          </div>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions style="max-width:690px;" class="py-4">
        <v-btn
            large
            plain
            color="primary"
            class="ml-2 px-5"
            @click="cancel">
          {{ cancelButtonText }}
        </v-btn>
        <v-spacer />
        <v-btn
            large
            color="primary"
            class="px-5"
            :disabled="!canConfirm"
            @click="confirm">
          {{ confirmButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import ClientGeoLocation from '@/components/app/ClientGeoLocation'

export default {
  name: 'DialogRescheduleOrderButton',

  components: {
    ClientGeoLocation
  },

  props: {
    order: {
      type: Object,
      required: true
    },
    requiresRouteChange: {
      type: Boolean,
      required: false
    },
    buttonText: {
      type: String,
      default: 'Reschedule',
      required: false
    },
    buttonStyle: {
      type: String,
      default: 'outlined',
      required: false
    },
    buttonWidth: {
      type: Number,
      required: false
    },
    fullscreen: {
      type: Boolean,
      default: false,
      required: false
    },
    confirmButtonText: {
      type: String,
      default: 'Confirm',
      required: false
    },
    cancelButtonText: {
      type: String,
      default: 'Cancel',
      required: false
    }
  },

  data () {
    return {
      enableTrueReRoute: false,
      isDialogOpen: false,
      notesReason: null,
      notesRouteChange: null
    }
  },

  computed: {
    shipper () {
      return this.order.shipper || {}
    },
    receiver () {
      return this.order.receiver || {}
    },
    dispatcherNotes () {
      return this.order.dispatcher_notes?.reschedule
    },
    routeChangeMessage () {
      return 'Your route is changing due to the reschedule.'
      // return (this.order.order_status === 'TO_SHIPPER')
      //   ? 'You are already in transit to pickup this order.'
      //   : 'You are already in transit to deliver this order.'
    },
    canConfirm () {
      if (this.requiresRouteChange) {
        const geoLocation = this.$refs['client-geo-location']
        const reRouteCoords = (geoLocation) ? geoLocation.getInfo() : {}
        const hasCoords = reRouteCoords.latitude && reRouteCoords.longitude
        return this.notesRouteChange && hasCoords
      } else {
        return true
      }
    }
  },

  beforeMount () {
    this.notesReason = this.dispatcherNotes
  },

  watch: {
    isDialogOpen (newVal) {
      if (newVal && this.requiresRouteChange) {
        const geoLocation = this.$refs['client-geo-location']
        if (geoLocation) geoLocation.loadClientCoordindates()
      }
    }
  },

  methods: {
    clear () {
      this.reason = 'delivery'
      this.notesReason = null
      this.notesRouteChange = null
    },

    cancel () {
      this.clear()
      this.isDialogOpen = false
      this.$emit('canceled', this.order)
    },

    confirm () {
      const geoLocation = this.$refs['client-geo-location']
      const reRouteCoords = (geoLocation) ? geoLocation.getInfo() : {}
      const hasCoords = reRouteCoords.latitude && reRouteCoords.longitude

      this.isDialogOpen = false

      this.$emit('confirmed', {
        order: this.order,
        notes: this.notesReason,
        notes_route_change: this.notesRouteChange,
        client_coords: (hasCoords) ? reRouteCoords : null
      })
    }
  }
}
</script>

<style scoped>
  .v-card {
    border: 1px solid #525252 !important;
  }

  .dialog-text {
    font-size: 18px !important;
  }

  .order-number {
    color: #f7a725;
  }

  .to-from-text {
    font-size: 1.08rem;
  }

  .info-text-main {
    font-size: 1.2rem;
  }

  .info-text-sub {
    font-size: 1.0rem !important;
  }

  .client-coords-container {
    border: 1px solid #f6a626;
    border-radius: 4px;
  }
</style>
